'use client';

import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import { sendWebEvent } from '@/lib/analytics/web-event-client';
import {
  gradeLabel,
  materialsCountLabel,
  pagesLabel,
  type LibraryGrade,
  type LibraryMaterial,
} from '@/lib/library/materials';
import { MaterialThumb } from './MaterialThumb';
import { useQueryParam } from './useQueryParam';
import { cabinetApi } from '@/lib/parent/cabinet-links';

type Filter = 'all' | LibraryGrade;

type Props = {
  materials: LibraryMaterial[];
  grades: LibraryGrade[];
  /**
   * public — каталог на сайте: public-материалы скачиваются, account ведут на
   * страницу материала с гейтом. parent — кабинет семьи: скачивается всё.
   */
  mode: 'public' | 'parent';
  initialFilter: Filter;
  /** Материал, ради которого пришли по ссылке из письма: подсветить и прокрутить. */
  highlightSlug?: string | null;
};

/**
 * Каталог библиотеки: пилюли класса (как фильтр блога) и сетка карточек.
 * Карточки рендерятся все, фильтр только прячет лишние, поэтому весь список
 * есть в HTML для поиска. Класс в URL (`?grade=3`) лишь состояние интерфейса,
 * canonical у страницы всегда `/biblioteka` (спека §10).
 */
export function LibraryCatalog({
  materials,
  grades,
  mode,
  initialFilter,
  highlightSlug,
}: Props) {
  // null = родитель ещё не нажимал фильтр: берём класс из `?grade=` (на
  // статической публичной странице он известен только после гидрации), иначе
  // стартовый.
  const [picked, setPicked] = useState<Filter | null>(null);
  const [downloaded, setDownloaded] = useState<Record<string, boolean>>({});
  const urlGrade = Number(useQueryParam('grade'));
  const urlFilter =
    mode === 'public' && (grades as number[]).includes(urlGrade) ? (urlGrade as LibraryGrade) : null;
  const filter: Filter = picked ?? urlFilter ?? initialFilter;

  const highlightRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    highlightRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, []);

  function pick(next: Filter) {
    setPicked(next);
    if (mode === 'public') {
      const url = new URL(window.location.href);
      if (next === 'all') url.searchParams.delete('grade');
      else url.searchParams.set('grade', String(next));
      window.history.replaceState(null, '', url.pathname + url.search);
    }
  }

  const visible = useMemo(
    () => materials.filter((m) => filter === 'all' || m.grade === filter),
    [materials, filter],
  );

  const options: Filter[] = grades.length > 1 ? ['all', ...grades] : grades;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b border-graphite-8 pb-5">
        {options.length > 1 && (
          <div
            className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1"
            role="group"
            aria-label="Фильтр по классу"
          >
            {options.map((opt) => {
              const active = filter === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  aria-pressed={active}
                  onClick={() => pick(opt)}
                  className={`min-h-[44px] shrink-0 whitespace-nowrap rounded-full border px-5 text-sm font-semibold transition-colors ${
                    active
                      ? 'border-ochre bg-ochre text-graphite'
                      : 'border-graphite-8 bg-cream-100 text-graphite-70 hover:border-graphite-15'
                  }`}
                >
                  {opt === 'all' ? 'Все' : gradeLabel(opt)}
                </button>
              );
            })}
          </div>
        )}
        <span className="text-[13.5px] text-graphite-55" aria-live="polite">
          {materialsCountLabel(visible.length)}
        </span>
      </div>

      <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5">
        {visible.map((m) => {
          const isHighlighted = m.slug === highlightSlug;
          return (
            <li key={m.slug}>
              <article
                ref={isHighlighted ? highlightRef : undefined}
                className={`flex h-full gap-4 rounded-[18px] border bg-white p-4 ${
                  isHighlighted ? 'border-ochre ring-2 ring-ochre/40' : 'border-graphite-8'
                }`}
              >
                {mode === 'public' ? (
                  // Дубль ссылки заголовка: мышью удобно, с клавиатуры не нужен.
                  <Link
                    href={`/biblioteka/${m.slug}`}
                    tabIndex={-1}
                    aria-hidden
                    className="w-[88px] shrink-0 sm:w-[104px]"
                  >
                    <MaterialThumb material={m} sizes="104px" />
                  </Link>
                ) : (
                  <div className="w-[88px] shrink-0 sm:w-[104px]">
                    <MaterialThumb material={m} sizes="104px" />
                  </div>
                )}
                <div className="flex min-w-0 flex-1 flex-col">
                  <p className="flex flex-wrap items-center gap-2 text-[12.5px] text-graphite-55">
                    <span>
                      {gradeLabel(m.grade)} · {m.topic}
                    </span>
                    {mode === 'public' && m.access === 'account' && (
                      <span className="rounded-full bg-cream-100 px-2 py-0.5 text-[11.5px] font-medium">
                        По аккаунту
                      </span>
                    )}
                  </p>
                  <h3 className="mt-2 font-display text-[17px] font-bold leading-tight tracking-[-0.015em] text-graphite">
                    {mode === 'public' ? (
                      <Link href={`/biblioteka/${m.slug}`} className="hover:text-teal-dark">
                        {m.shortTitle}
                      </Link>
                    ) : (
                      m.shortTitle
                    )}
                  </h3>
                  <p className="mt-1.5 text-[13.5px] leading-normal text-graphite-70">
                    {m.description}
                  </p>
                  <p className="mt-2 text-[12.5px] text-graphite-50">
                    PDF · {pagesLabel(m.pageCount)}
                  </p>
                  <div className="mt-auto pt-3">
                    <CardAction
                      material={m}
                      mode={mode}
                      done={Boolean(downloaded[m.slug])}
                      onDownload={() => setDownloaded((s) => ({ ...s, [m.slug]: true }))}
                    />
                  </div>
                </div>
              </article>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function CardAction({
  material,
  mode,
  done,
  onDownload,
}: {
  material: LibraryMaterial;
  mode: 'public' | 'parent';
  done: boolean;
  onDownload: () => void;
}) {
  const href =
    mode === 'parent'
      ? cabinetApi.libraryDownload(material.slug)
      : `/api/library/${material.slug}/download?from=catalog`;

  if (done) {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="inline-flex min-h-[40px] items-center rounded-full bg-badge-done-bg px-3.5 text-[13px] font-semibold text-badge-done-txt">
          Файл скачан
        </span>
        <a href={href} className="text-[13px] font-semibold text-teal-dark hover:text-graphite">
          Скачать снова
        </a>
      </div>
    );
  }

  if (mode === 'public' && material.access === 'account') {
    return (
      <Link
        href={`/biblioteka/${material.slug}`}
        className="inline-flex min-h-[44px] w-full items-center justify-center rounded-3xl border border-graphite-15 px-4 text-[13.5px] font-semibold text-graphite transition-colors hover:border-graphite-35"
      >
        Открыть с аккаунтом
      </Link>
    );
  }

  return (
    // Обычная ссылка без download-атрибута: сервер отдаёт файл как вложение,
    // страница остаётся на месте, а при ошибке сервер вернёт на страницу
    // материала с понятным сообщением вместо сохранённого «битого» файла.
    <a
      href={href}
      onClick={onDownload}
      className={`inline-flex min-h-[44px] w-full items-center justify-center rounded-3xl px-4 text-[13.5px] font-semibold transition-colors ${
        mode === 'parent'
          ? 'bg-ochre-tint text-graphite hover:bg-ochre-soft'
          : 'bg-teal text-white hover:bg-teal-dark'
      }`}
    >
      {mode === 'parent' ? 'Скачать PDF' : 'Скачать бесплатно'}
    </a>
  );
}

/** Клик по CTA со следом в аналитике (регистрация, вход, тренажёр). */
export function TrackedLink({
  href,
  className,
  children,
  event,
}: {
  href: string;
  className?: string;
  children: React.ReactNode;
  event: Parameters<typeof sendWebEvent>[0];
}) {
  return (
    <Link href={href} className={className} onClick={() => sendWebEvent(event)}>
      {children}
    </Link>
  );
}
